import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { APP_NAME, BrandMark } from "../../config/brand";
import { useAuth } from "../../providers/AuthProvider";
import { useLogout } from "../../hooks/useLogout";
import { Sidebar } from "./Sidebar";
import { Button } from "../ui/Button";

/**
 * Shell for the admin Analytics screens: persistent Sidebar (desktop) + a
 * compact mobile top bar carrying brand, display name, and logout where the
 * sidebar is hidden.
 */
export function AnalyticsLayout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { user, config } = useAuth();
  const logout = useLogout();
  const appName = config?.appName ?? APP_NAME;
  const displayName = user?.displayName ?? user?.username ?? "Admin";

  return (
    <div className="flex min-h-full">
      <Sidebar />

      <div className="flex min-h-full min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-surface-border bg-surface px-gutter py-3 md:hidden">
          <button
            type="button"
            onClick={() => navigate("/analytics")}
            className="flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <BrandMark className="h-6 w-6 text-brand-600" />
            <span className="text-brand font-semibold text-text">{appName}</span>
          </button>
          <div className="flex items-center gap-2">
            <span className="text-body font-medium text-text" title={displayName}>
              {displayName}
            </span>
            <Button variant="ghost" size="sm" onClick={() => void logout()}>
              Logout
            </Button>
          </div>
        </header>

        <main className="mx-auto w-full max-w-6xl flex-1 px-gutter py-6">{children}</main>
      </div>
    </div>
  );
}
